// Per-NUTRIENT soil-sample point layers: instead of one "Soil samples" layer + a per-sample list
// each nutrient with data becomes its own toggleable layer of value-colored points, with the value
// LABELED above each point (no hover needed). When 8-inch depth-normalized values exist (Depth Norm
// chain, run backend-side) and the event wasn't sampled at 8", a SECOND "<nutrient> · 8-inch
// normalized" layer appears — that's the version the ML model consumes.
import type {
  ManifestLayer,
  SampleEvent,
  SoilProperties,
  SoilSamplePoint,
} from "../types/contract";
import { toM3Factor, type FactorAnalyte } from "./extractantFactors";
import { extractantFactors } from "../hooks/useExtractantFactors";
import { estimateAtDepth, cecAtDepth, omAtDepth } from "./depthEstimate";
import { layerSkipsSoilLens, lensedValue } from "./mapValues";
import { soilUnits } from "../stores/soilUnitsStore";
import { needsDepthConversion } from "./depthDisplay";
import { lbsPerPpmAt } from "./soilUnits";
import { zoneVersionIdOf } from "./targetLayers";

// `lensless`: the extractant/depth lens question is MEANINGLESS for this analyte (a lab buffer
// index a dimensionless percent) — the layer never attempts M3-at-depth and never wears the lens
// suffix rather than "failing" to lens and stamping '· stored basis (extractant unknown)' noise on
// a number that has no other basis to be on — buffer pH + the saturation block get layers.
const NUTRIENTS: {
  key: string;
  label: string;
  unit?: string;
  lensless?: true;
}[] = [
  { key: "ph", label: "pH" },
  { key: "buffer_ph", label: "Buffer pH", lensless: true },
  { key: "om", label: "Organic matter", unit: "%" },
  { key: "cec", label: "CEC", unit: "meq/100g" },
  { key: "p", label: "Phosphorus (P)", unit: "ppm" },
  { key: "k", label: "Potassium (K)", unit: "ppm" },
  { key: "s", label: "Sulfur (S)", unit: "ppm" },
  { key: "ca", label: "Calcium (Ca)", unit: "ppm" },
  { key: "mg", label: "Magnesium (Mg)", unit: "ppm" },
  { key: "zn", label: "Zinc (Zn)", unit: "ppm" },
  { key: "b", label: "Boron (B)", unit: "ppm" },
  { key: "mn", label: "Manganese (Mn)", unit: "ppm" },
  // The saturation block (FC-only analytes, served through SS6's properties merge). Layers list only
  // when data exists, so fields without lab-reported saturations see no extra rows.
  { key: "k_sat_pct", label: "K saturation", unit: "%", lensless: true },
  { key: "ca_sat_pct", label: "Ca saturation", unit: "%", lensless: true },
  { key: "mg_sat_pct", label: "Mg saturation", unit: "%", lensless: true },
  { key: "h_sat_pct", label: "H saturation", unit: "%", lensless: true },
  { key: "na_sat_pct", label: "Na saturation", unit: "%", lensless: true },
  { key: "base_sat_pct", label: "Base saturation", unit: "%", lensless: true },
];

// Keys DELETE .../results?nutrient= accepts. A Visualize nutrient outside this
// set cannot offer "only this nutrient": the route answers 422.
export const SOIL_RESULT_DELETE_KEYS = [
  "ph",
  "buffer_ph",
  "om",
  "cec",
  "p",
  "k",
  "s",
  "ca",
  "mg",
  "zn",
  "b",
  "mn",
  "k_sat_pct",
  "ca_sat_pct",
  "mg_sat_pct",
  "h_sat_pct",
  "na_sat_pct",
  "base_sat_pct",
] as const;

const SERVER_NUTRIENT_KEYS = new Set<string>(SOIL_RESULT_DELETE_KEYS);

export function soilResultDeleteKey(
  key: string | null | undefined,
): string | null {
  if (!key || !SERVER_NUTRIENT_KEYS.has(key)) return null;
  return key;
}

export function soilNutrientKeys(): readonly string[] {
  return NUTRIENTS.map((n) => n.key);
}

export function soilNutrientLabel(key: string): string {
  for (const n of NUTRIENTS) if (n.key === key) return n.label;
  return key;
}

export const SOIL_LAYER_PREFIX = "soil-";
// OLD SAMPLES: layers from a NON-current (older) results-bearing event get this id prefix
// (`soil-old-<eventId>-<nutrient>`) so `CovariateList` can group them under an "Old samples"
// folder.
export const OLD_SOIL_PREFIX = "soil-old-";
export const isOldSoilLayer = (id: string): boolean =>
  id.startsWith(OLD_SOIL_PREFIX);

// Current nutrient rows are `soil-<nutrient>`. Older events are `soil-old-…`.
// The retired all-in-one `soil-samples` row is neither.
export function splitSoilSampleLayers<T extends { id?: string }>(
  layers: T[],
): { current: T[]; old: T[] } {
  const current: T[] = [];
  const old: T[] = [];
  for (const layer of layers) {
    const id = layer.id ?? "";
    if (isOldSoilLayer(id)) {
      old.push(layer);
      continue;
    }
    if (id.startsWith(SOIL_LAYER_PREFIX) && id !== "soil-samples")
      current.push(layer);
  }
  return { current, old };
}

const NUTRIENT_KEYS = new Set(NUTRIENTS.map((n) => n.key));

function knownResultKey(key: string): boolean {
  const base = key.endsWith("-8in") ? key.slice(0, -"-8in".length) : key;
  return NUTRIENT_KEYS.has(base);
}

// Which result layer a completed order should solo. Current-form ids
// (`soil-<key>`) belong only to `currentEventId`. Any other event is
// `soil-old-<eventId>-<key>` (event ids may contain hyphens, so the remainder
// has to be a real analyte, not a longer sibling id). Current-form layers are
// never a stand-in for a different event.
function resultCandidates(
  layers: readonly { id?: string | null }[],
  sampleEventId: string,
): { id: string; key: string; old: boolean }[] {
  const oldPrefix = `${OLD_SOIL_PREFIX}${sampleEventId}-`;
  const parsed: { id: string; key: string; old: boolean }[] = [];
  for (const layer of layers) {
    const id = layer.id;
    if (!id || id === "soil-samples" || !id.startsWith(SOIL_LAYER_PREFIX))
      continue;
    if (id.startsWith(OLD_SOIL_PREFIX)) {
      if (!id.startsWith(oldPrefix)) continue;
      const key = id.slice(oldPrefix.length);
      if (knownResultKey(key)) parsed.push({ id, key, old: true });
      continue;
    }
    const key = id.slice(SOIL_LAYER_PREFIX.length);
    if (knownResultKey(key)) parsed.push({ id, key, old: false });
  }
  return parsed;
}

export function resolveResultLayerId(
  layers: readonly { id?: string | null }[],
  sampleEventId: string,
  prefer: readonly string[] = ["ph", "om"],
  currentEventId: string | null = null,
): string | null {
  const parsed = resultCandidates(layers, sampleEventId);
  const pool =
    sampleEventId === currentEventId
      ? parsed.filter((p) => !p.old)
      : parsed.filter((p) => p.old);
  if (pool.length === 0) return null;
  for (const want of prefer) {
    const bare = pool.find((p) => p.key === want);
    if (bare) return bare.id;
    const normalized = pool.find((p) => p.key === `${want}-8in`);
    if (normalized) return normalized.id;
  }
  return pool[0]?.id ?? null;
}

// Resolve which OLD event a visible `soil-old-<eventId>-<nutrient>[-8in]` layer belongs to, given the field's known
// old event ids. Event ids can contain hyphens (e.g. `points:loop65-test`), so a plain prefix match can be satisfied
// by a SHORTER sibling id (`points:loop65`) — pick the LONGEST matching id, unambiguously the true owner.
export function matchOldEventId(
  layerId: string,
  oldEventIds: string[],
): string | null {
  let best: string | null = null;
  for (const eid of oldEventIds) {
    if (
      layerId.startsWith(`${OLD_SOIL_PREFIX}${eid}-`) &&
      (best === null || eid.length > best.length)
    )
      best = eid;
  }
  return best;
}

const lensActiveNow = () => {
  const u = soilUnits();
  return (
    needsDepthConversion(u.depthIn) ||
    u.pExtractant !== "m3" ||
    u.kExtractant !== "m3"
  );
};

const _warnedLayers = new Set<string>();
const warnedNoGps = new Set<string>();
function warnNoGpsOnce(id: string, n: number): void {
  if (warnedNoGps.has(id)) return;
  warnedNoGps.add(id);
  console.warn(
    `[soilLayers] ${id}: ${n} sample(s) carry values but NO GPS coordinates - stored and served, but unmappable, so no point layer is listed. Bulk name-attached sets land here until sample locations exist (e.g. a point_id join to the ordered target points).`,
  );
}
function warnUnlensedOnce(layerId: string, unresolved: number, total: number) {
  if (_warnedLayers.has(layerId)) return;
  _warnedLayers.add(layerId);
  console.warn(
    `[soil samples] "${layerId}" is showing STORED values, not your selected depth/extractant: ` +
      `${unresolved}/${total} points could not be put on a Mehlich-3 basis at that depth. ` +
      `Usual causes: no modelled CEC or OM on the sample (the chain root), or the nutrient has no ` +
      `depth model (S/Zn/Mn/B/Ca/Mg). The whole layer stays on stored values so its labels, legend and ` +
      `pins cannot disagree.`,
  );
}

// magnitude-aware point label (matches the hover formatting)
export function labelOf(v: number): string {
  const abs = Math.abs(v);
  return abs >= 100 ? v.toFixed(0) : abs >= 10 ? v.toFixed(1) : v.toFixed(2);
}

/*
 *
 * That is ingest's own order, and both factor directions are published precisely so neither has to
 * be `null` = this sample cannot be put on a known basis, and the caller must show the stored value
 * unconverted rather than guess. Two causes, both deliberate: a NULL token (the lab string could
 * not be mapped with confidence) and `bray_olsen`, which means *Bray or Olsen, UNDETERMINED* — they
 * carry different factors, so picking one moves a real number in a real recommendation.
 */
export function sampleBlobToM3(
  s: SoilSamplePoint,
): Record<string, number> | null {
  // When it is absent but the sample was PULLED at 8", the measured blob is the 8" blob depth-wise
  // but its MASS values are in the row's own units, so a `lbs_ac` row must convert (÷2.4 at 8")
  // before anything treats it as ppm.
  let p8 = s.properties_8in ?? (s.depth_in === 8 ? s.properties : null);
  if (!p8) return null;
  if (!s.properties_8in && s.units === "lbs_ac") {
    const conv: Record<string, number> = {};
    for (const [k, v] of Object.entries(p8)) {
      if (typeof v !== "number" || !Number.isFinite(v)) continue;
      // dimensionless keys (pH family, percents, CEC) are never on a mass basis — ÷2.4 would corrupt them
      conv[k] =
        k === "ph" ||
        k === "om" ||
        k === "cec" ||
        k === "buffer_ph" ||
        k.endsWith("_sat_pct")
          ? v
          : v / lbsPerPpmAt(8);
    }
    p8 = conv as typeof p8;
  }
  const f = extractantFactors();
  const out: Record<string, number> = {};
  // pH / OM / CEC are not extractant-dependent in the published factor table — carried straight across.
  for (const k of ["ph", "om", "cec"] as const) {
    const v = p8[k];
    if (typeof v === "number" && Number.isFinite(v)) out[k] = v;
  }
  // P follows the phosphorus token; K/Ca/Mg follow the cation token, each with its OWN factor.
  const pairs: [FactorAnalyte, string | null | undefined][] = [
    ["p", s.p_extractant],
    ["k", s.k_extractant],
    ["ca", s.k_extractant],
    ["mg", s.k_extractant],
  ];
  for (const [analyte, token] of pairs) {
    const v = p8[analyte];
    if (typeof v !== "number" || !Number.isFinite(v)) continue;
    // This is the one place a null factor means identity rather than refusal, and it is only
    // correct because ingest made the same call on the same input.
    out[analyte] = v * (toM3Factor(f, analyte, token) ?? 1);
  }
  return out;
}

export function sampleM3AtDepth(
  sample: SoilSamplePoint,
  nutrientKey: string,
): number | null {
  const blob = sampleBlobToM3(sample);
  if (!blob) return null;
  const cec8 = blob.cec;
  const om8 = blob.om;
  if (!Number.isFinite(cec8) || !Number.isFinite(om8)) return null;

  const depth = soilUnits().depthIn;
  if (!needsDepthConversion(depth)) return blob[nutrientKey] ?? null; // 8" is an identity for every property
  if (nutrientKey === "cec") return cecAtDepth(depth, cec8);
  if (nutrientKey === "om") return omAtDepth(depth, om8, cec8);
  if (nutrientKey === "p" || nutrientKey === "k" || nutrientKey === "ph") {
    const est = estimateAtDepth(depth, {
      cec8,
      om8,
      ph: blob.ph,
      p: blob.p,
      k: blob.k,
      ca: blob.ca,
      mg: blob.mg,
    });
    const v = nutrientKey === "ph" ? est.ph : est[nutrientKey as "p" | "k"];
    return v ?? null;
  }
  // S / Zn / Mn / B / Ca / Mg have no target model in the chain — no at-depth value exists for them.
  return null;
}

/*
 *
 * TRUE for the per-nutrient SAMPLE layers ('soil-*' current and old): a zone-attached reading
 * covers every point of its polygon and a dot is the measurement itself — suppressing them in
 * favour of the MODELLED estimate — the pre- rule built for stored-8″ COG maps) left the whole
 * hover as em-dashes on zone-sampled fields, where the estimate's CEC/OM chain root often cannot be
 * read at all.
 */
export function hoverShowsSample(layer: { id?: string | null }): boolean {
  return (layer.id ?? "").startsWith(SOIL_LAYER_PREFIX);
}

/*
 *
 * The value the DEPTH-CHAIN predictor blob wants from a hovered feature: STORED M3-8″ — compute's
 * coefficients are fitted there.
 */
export function predictorValueOf(
  props: Record<string, unknown> | null | undefined,
  raw: number | null,
): number | null {
  const vs = props?.value_stored;
  if (typeof vs === "number" && Number.isFinite(vs)) return vs;
  if (vs != null) {
    const n = Number(vs);
    if (Number.isFinite(n)) return n;
  }
  return raw;
}

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
export function fmtSampleDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso.slice(0, 10);
  return `${MONTHS[Number(m[2]) - 1] ?? m[2]} ${Number(m[3])}, ${m[1]}`;
}

// OLD = attributed to a different results-bearing, non-current event.
// Unattributed points, unknown events, and events with no results stay current.
function sampleIsOld(
  sample: SoilSamplePoint,
  events: readonly SampleEvent[],
  currentId: string,
): boolean {
  const eid = sample.sample_event_id;
  if (eid == null || eid === currentId) return false;
  const ev = events.find((e) => e.event_id === eid);
  return !!ev?.has_results && !ev.is_current;
}

function hasNutrient(sample: SoilSamplePoint, key: string): boolean {
  return (
    typeof sample.properties?.[key] === "number" ||
    typeof sample.properties_8in?.[key] === "number"
  );
}

export function soilEventDateLabel(
  events: readonly SampleEvent[] | undefined,
  eventId: string,
): string {
  return fmtSampleDate(
    events?.find((e) => e.event_id === eventId)?.sample_date,
  );
}

export function soilEventPointCount(
  samples: readonly SoilSamplePoint[],
  eventId: string,
): number {
  let n = 0;
  for (const s of samples) if (s.sample_event_id === eventId) n += 1;
  return n;
}

// A current row can draw points that are not its event. The confirm says so.
export function soilRowMixesEvents(
  samples: readonly SoilSamplePoint[],
  events: readonly SampleEvent[] | undefined,
  layer: {
    id: string;
    sample_event_id?: string | null;
    nutrient_key?: string | null;
  },
): boolean {
  const eventId = layer.sample_event_id;
  const key = layer.nutrient_key;
  if (!eventId || !key || !events || isOldSoilLayer(layer.id)) return false;
  const current = events.find((e) => e.is_current);
  if (!current || current.event_id !== eventId) return false;
  for (const s of samples) {
    if (s.sample_event_id === eventId || sampleIsOld(s, events, eventId))
      continue;
    if (hasNutrient(s, key)) return true;
  }
  return false;
}

function clearNutrient(
  props: SoilProperties | null | undefined,
  key: string,
): SoilProperties | null | undefined {
  if (!props || typeof props[key] !== "number") return props;
  const next = { ...props };
  delete next[key];
  return next;
}

function clearSampleNutrient(
  sample: SoilSamplePoint,
  eventId: string,
  nutrient: string,
): SoilSamplePoint {
  if (sample.sample_event_id !== eventId) return sample;
  const properties = clearNutrient(sample.properties, nutrient);
  const properties_8in = clearNutrient(sample.properties_8in, nutrient);
  if (
    properties === sample.properties &&
    properties_8in === sample.properties_8in
  )
    return sample;
  return { ...sample, properties: properties ?? null, properties_8in };
}

// One nutrient clears measured and 8-inch values on that event only.
// Null nutrient drops that event's points. Another event on the same date stays.
export function dropSoilResults(
  samples: readonly SoilSamplePoint[],
  eventId: string,
  nutrient: string | null,
): SoilSamplePoint[] {
  if (nutrient == null)
    return samples.filter((s) => s.sample_event_id !== eventId);
  return samples.map((s) => clearSampleNutrient(s, eventId, nutrient));
}

export function soilDeleteLayerIds(
  layers: readonly {
    id: string;
    sample_event_id?: string | null;
    nutrient_key?: string | null;
  }[],
  eventId: string,
  nutrient: string | null,
): string[] {
  const ids: string[] = [];
  for (const layer of layers) {
    if (layer.sample_event_id !== eventId) continue;
    if (nutrient != null && layer.nutrient_key !== nutrient) continue;
    ids.push(layer.id);
  }
  return ids;
}

type LayerEvent = { eventId: string; date: string | null; isOld: boolean };

/*
 *
 * ZONE-SAMPLED RESULTS ARE AREAL, AND MUST DRAW AS THE ZONE — *"it shouldn't just sho the zone's
 * centroid.
 */
export type ZoneGeomIndex = ReadonlyMap<number, GeoJSON.Geometry>;

/*
 *
 * Feature ORDER is the fallback and only when NO feature carries a numeric `zone`: both reads are
 * `ORDER BY z.
 */
export function zoneGeomIndex(
  fc: GeoJSON.FeatureCollection | null | undefined,
): ZoneGeomIndex {
  const out = new Map<number, GeoJSON.Geometry>();
  const feats = fc?.features ?? [];
  const ordinalOf = (f: GeoJSON.Feature): unknown =>
    (f.properties as Record<string, unknown> | null)?.zone;
  const explicit = feats.some(
    (f) =>
      typeof ordinalOf(f) === "number" &&
      Number.isFinite(ordinalOf(f) as number),
  );
  feats.forEach((f, i) => {
    const g = f.geometry;
    if (!g || (g.type !== "Polygon" && g.type !== "MultiPolygon")) return;
    const z = ordinalOf(f);
    const ordinal = explicit
      ? typeof z === "number" && Number.isFinite(z)
        ? z
        : null
      : i + 1;
    if (ordinal != null && !out.has(ordinal)) out.set(ordinal, g);
  });
  return out;
}

/* * Every zones version's polygons, by version id — built ONCE per manifest, read per nutrient layer. */
export function zoneGeomsByVersion(
  fcByVersion: ReadonlyMap<string, GeoJSON.FeatureCollection> | undefined,
): Map<string, ZoneGeomIndex> {
  const out = new Map<string, ZoneGeomIndex>();
  for (const [vid, fc] of fcByVersion ?? []) {
    const idx = zoneGeomIndex(fc);
    if (idx.size) out.set(vid, idx);
  }
  return out;
}

/* * The polygon this sample's reading describes, or null when it is not a zone-attached reading. */
function zonePolygonOf(
  s: SoilSamplePoint,
  byVersion: ReadonlyMap<string, ZoneGeomIndex>,
): GeoJSON.Geometry | null {
  if (s.zone_ref == null || !s.sample_event_id) return null;
  const vid = zoneVersionIdOf(s.sample_event_id);
  if (!vid) return null;
  return byVersion.get(vid)?.get(s.zone_ref) ?? null;
}

function nutrientLayer(
  samples: SoilSamplePoint[],
  n: { key: string; label: string; unit?: string; lensless?: true },
  variant: "measured" | "8in",
  event?: LayerEvent,
  zoneGeoms?: ReadonlyMap<string, ZoneGeomIndex>,
): ManifestLayer | null {
  const propsOf = (s: SoilSamplePoint): SoilProperties | null | undefined =>
    variant === "8in" ? s.properties_8in : s.properties;
  const valued = samples.filter((s) => typeof propsOf(s)?.[n.key] === "number");
  // AREAL OR POINT IS DECIDED PER LAYER, NOT PER SAMPLE — the same rule the lens follows two blocks
  // down, and for a harder reason: `geometryKindOf` picks ONE render branch for the whole
  // FeatureCollection, so a mixed layer would draw some readings and silently drop the rest.
  const polyOf = new Map<SoilSamplePoint, GeoJSON.Geometry>();
  if (zoneGeoms?.size)
    for (const s of valued) {
      const g = zonePolygonOf(s, zoneGeoms);
      if (g) polyOf.set(s, g);
    }
  const areal = valued.length > 0 && polyOf.size === valued.length;
  // A sample that cannot be placed cannot be mapped; the layer only lists when something can
  // actually draw, and the skip says why (once) instead of failing silently. A ZONE READING NEEDS
  // NO GPS: its ground is the polygon, which the zone version supplies.
  const pts = areal
    ? valued
    : valued.filter((s) => Number.isFinite(s.lon) && Number.isFinite(s.lat));
  if (valued.length && !pts.length) {
    warnNoGpsOnce(`${SOIL_LAYER_PREFIX}${n.key}`, valued.length);
    return null;
  }
  if (!pts.length) return null;
  // OLD events get a distinct id (per event) so they can be grouped; the SAMPLE DATE is suffixed onto the title
  // for every results-bearing layer (current + old) so each reads with when it was pulled.
  const idBase = event?.isOld
    ? `${OLD_SOIL_PREFIX}${event.eventId}-${n.key}`
    : `${SOIL_LAYER_PREFIX}${n.key}`;
  const dateTag = event?.date
    ? ` · ${fmtSampleDate(event.date)}`
    : event?.isOld
      ? " · older"
      : "";
  const id = `${idBase}${variant === "8in" ? "-8in" : ""}`;
  const baseTitle =
    variant === "8in"
      ? `${n.label} · 8-inch normalized${dateTag}`
      : `${n.label} (samples${dateTag})`;

  // If any point cannot be put on M3 — an unmappable lab token, or `bray_olsen` meaning
  // *undetermined* — the WHOLE layer stays on stored values. The legend and the pins apply the lens
  // per LAYER, so lensing only some points would put a converted label under an unconverted tick
  // with nothing on screen to say which was which.
  const m3 = n.lensless ? [] : pts.map((s) => sampleM3AtDepth(s, n.key));
  const lensed = !n.lensless && m3.every((v) => v != null);
  // SAY WHY when a layer silently declines to follow the picker. This feature has now failed twice by doing
  // nothing at all — once because only the `8in` variant was lensed (and it isn't built for 8" fields), once
  // because an absent extractant token refused every real sample. Both looked identical on screen: numbers
  // that just sit there. A one-line reason turns the next occurrence into a fact instead of a guess.
  if (!n.lensless && !lensed && lensActiveNow())
    warnUnlensedOnce(
      id,
      pts.length - m3.filter((v) => v != null).length,
      pts.length,
    );
  const u = soilUnits();
  const lensActive =
    needsDepthConversion(u.depthIn) ||
    u.pExtractant !== "m3" ||
    u.kExtractant !== "m3";
  const lensTag =
    n.lensless || !lensActive
      ? ""
      : lensed
        ? ` · at ${u.depthIn}″`
        : " · stored basis (extractant unknown)";

  // The feature value is M3 AT DEPTH — the extractant is applied downstream, once, by the shared pipeline.
  const features = pts.map((s, i) => {
    const v = (lensed ? m3[i] : null) ?? (propsOf(s)![n.key] as number);
    // the STORED-basis twin: analytical consumers — the split's kriged underlay Compare, Check
    // Accuracy's dot overlay — run on stored M3-8″ like every surface they sit against while the
    // visible dot keeps following the picker.
    const stored = n.lensless ? null : (sampleBlobToM3(s)?.[n.key] ?? null);
    return {
      type: "Feature",
      properties: {
        value: v,
        value_stored: stored ?? (propsOf(s)![n.key] as number),
        value_measured: propsOf(s)![n.key] as number,
        label: labelOf(
          n.lensless || layerSkipsSoilLens({ id, title: baseTitle })
            ? v
            : lensed
              ? lensedValue(
                  { id, title: baseTitle, unit: n.unit } as ManifestLayer,
                  v,
                ).value
              : v,
        ),
        point_id: s.point_id,
        ...(areal ? { zone_ref: s.zone_ref } : null),
      },
      // the ZONE this reading describes, when it describes one — else the point it was pulled at
      geometry: areal
        ? polyOf.get(s)!
        : { type: "Point", coordinates: [s.lon, s.lat] },
    };
  });
  // The ramp spans what is DRAWN. Scaling colours to the stored range while labels show converted numbers
  // would put a point's colour and its own label in visible disagreement.
  const shownValues = features.map((f) => f.properties.value as number);

  // 🔎 [zone-samples] permanent debug trace: one line per baked sample layer so a blank map can be
  // split into “the bake is wrong” vs “the render is wrong” from the console alone.
  console.debug(
    `[zone-samples] bake ${id}: n=${features.length} areal=${areal} lensed=${lensed} ` +
      `geo=${features[0] ? features[0].geometry.type : "-"} vmin=${Math.min(...shownValues)} vmax=${Math.max(...shownValues)} ` +
      `label0=${features[0] ? JSON.stringify((features[0].properties as { label?: string }).label) : "-"} ` +
      `value0=${features[0] ? (features[0].properties as { value?: number }).value : "-"}`,
  );
  return {
    id,
    kind: "geojson",
    title: `${baseTitle}${lensTag}`,
    inline: { type: "FeatureCollection", features },
    colormap: "viridis",
    // Tells the pins and the legend that these numbers are ALREADY at the display depth, so they must not
    // re-solve them from the modelled surfaces (a different data source entirely — see contract.ts).
    atDisplayDepth: lensed && lensActive,
    unlensed: !n.lensless && !lensed,
    lensless: !!n.lensless,
    value_min: Math.min(...shownValues),
    value_max: Math.max(...shownValues),
    unit: n.unit,
    // Point sample layers have always drawn it; the areal branch shares its render path with the
    // per-cell covariate choropleths, which must NOT be labelled (a hexgrid is thousands of cells)
    // so the request is explicit rather than inferred from geometry.
    valueLabels: true,
    default_visible: false,
    default_opacity: 1,
    order: 210, // above everything raster/zonal; sample points read on top
    // Delete addresses this event and this analyte. The layer id is not parsed:
    // event ids contain colons (`bulk:<session>:<uid>`).
    sample_event_id: event?.eventId,
    nutrient_key: n.key,
    content_version: "client",
    is_stale: false,
  };
}

// One layer per nutrient with data, plus its 8-inch-normalized sibling when normalized values exist AND at least
// one sample wasn't taken at 8" (at 8" the chain is identity, so a duplicate layer would be pure noise).
function layersForEvent(
  samples: SoilSamplePoint[],
  event: LayerEvent | null,
  zoneGeoms?: ReadonlyMap<string, ZoneGeomIndex>,
): ManifestLayer[] {
  const out: ManifestLayer[] = [];
  const anyOffDepth = samples.some(
    (s) => s.depth_in !== 8 && s.properties_8in != null,
  );
  for (const n of NUTRIENTS) {
    const measured = nutrientLayer(
      samples,
      n,
      "measured",
      event ?? undefined,
      zoneGeoms,
    );
    if (measured) out.push(measured);
    if (anyOffDepth) {
      const norm = nutrientLayer(
        samples,
        n,
        "8in",
        event ?? undefined,
        zoneGeoms,
      );
      if (norm) out.push(norm);
    }
  }
  return out;
}

// GRACEFUL: with no event attribution (backend hasn't shipped point `sample_event_id` / event
// `is_current`) every sample is treated as current, exactly as before — no old folder appears.
export function buildNutrientLayers(
  samples: SoilSamplePoint[],
  events?: SampleEvent[],
  /*
   *
   * Every zones version's polygons by version id (`useZoneVersionLayers.fcByVersion`). ABSENT OR
   * STILL LOADING IS ORDINARY, not an error: the layers build as points exactly as before and rebuild
   * as zones when the geometry lands, so a slow Z1b read costs a redraw, never a missing layer.
   */
  zonesByVersion?: ReadonlyMap<string, GeoJSON.FeatureCollection>,
): ManifestLayer[] {
  const zoneGeoms = zoneGeomsByVersion(zonesByVersion);
  const currentEvent = events?.find((e) => e.is_current);
  const currentId = currentEvent?.event_id;
  const attributed = samples.some((s) => s.sample_event_id != null);
  if (!currentId || !attributed)
    return layersForEvent(samples, null, zoneGeoms); // can't split → all current, no dates (unchanged)

  const current: SoilSamplePoint[] = [];
  const byOldEvent = new Map<string, SoilSamplePoint[]>();
  for (const s of samples) {
    // OLD = attributed to a DIFFERENT results-bearing, non-current event; everything else (incl. unattributed) = current
    if (sampleIsOld(s, events!, currentId)) {
      const eid = s.sample_event_id as string;
      const arr = byOldEvent.get(eid);
      if (arr) arr.push(s);
      else byOldEvent.set(eid, [s]);
    } else {
      current.push(s);
    }
  }
  // the CURRENT event keeps bare `soil-<nutrient>` ids (backward-compat) but now carries its sample date in the title
  const out = layersForEvent(
    current,
    {
      eventId: currentId,
      date: currentEvent!.sample_date ?? null,
      isOld: false,
    },
    zoneGeoms,
  );
  // newest old events first, so the folder reads most-recent-down
  const oldIds = [...byOldEvent.keys()].sort((a, b) => {
    const da = events!.find((e) => e.event_id === a)?.sample_date ?? "";
    const db = events!.find((e) => e.event_id === b)?.sample_date ?? "";
    return db.localeCompare(da);
  });
  for (const eid of oldIds) {
    const ev = events!.find((e) => e.event_id === eid)!;
    out.push(
      ...layersForEvent(
        byOldEvent.get(eid)!,
        { eventId: eid, date: ev.sample_date ?? null, isOld: true },
        zoneGeoms,
      ),
    );
  }
  return out;
}
